import { useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppContext } from "app/AppContext";

import DateInput from "components/form/input/DateInput";
import SelectInput from "components/form/input/SelectInput";
import TextInput from "components/form/input/TextInput";
import FForm, { useFormApi } from "components/form";
import FormContent from "components/form/FormContent";
import FormRow from "components/form/FormRow";
import TabContainer from "components/tabs/TabContainer";
import TabPanel from "components/tabs/TabPanel";
import ImageInput from "components/form/input/ImageInput";
import CheckboxInput from "components/form/input/CheckboxInput";
import EntitySearchInput from "components/form/EntitySearch/EntitySearchInput";
import DebugObjComp from "components/DebugObjComp";
import Anexo from "components/AnexosComp/components/Anexo";
import ApontamentoEditor from "./ApontamentoEditor";

import { useEntity } from "entity";
import LaudoVisita from "entity/LaudoVisita";
import Fazenda from "entity/Fazenda";
import Safra from "entity/Safra";
import Cultura from "entity/Cultura";
import LaudoVisitaReport from "entity/LaudoVisita/reports/LaudoVisitaReport";
import SeletorFazenda from "entity/Fazenda/components/SeletorFazenda";
import User from "entity/User";

import { shallowDiff } from "util/object";
import { validationLaudoVisita } from "../../functions"
import { DebugSetVisita, DebugVisitaConsole } from "./functions"
import useNotification from "notification";
import { useCoordinates } from "util/geolocation";
import _ from 'lodash'

import { Box, CircularProgress } from "@mui/material";
import { flog } from "util/FileLogger";
import { getCollectionApi } from "dbApi";

export default function LaudoVisitaForm(props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { showSuccess, showWarning, showError } = useNotification();

    const { useSetTitle, setActionsView } = useAppContext();
    const form = useFormApi();

    const visitaUid = searchParams.get("uid")

    const { entity, setEntity } = useEntity(LaudoVisita, visitaUid);
    // entity.data.apontamentos

    const [coordinates] = useCoordinates()

    const loadTecnico = useCallback(async () => {
        const tecnicoAux = (await User.getCurrent()).data
        form.api.change("tecnico", tecnicoAux)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (searchParams.get("uid") === null) {
            if (!form.getValues().localizacaoInicial)
                form.api.change("localizacaoInicial", coordinates)
            if (!form.getValues().tecnico)
                loadTecnico()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.getValues(), searchParams.get("uid")])

    useEffect(() => {
        if (!entity.loading) {


            const actions = [
                {
                    uid: "novoForm",
                    text: "Novo",
                    iconName: "Add",
                    sequence: "10",
                    action: async () => {
                        let newV = await LaudoVisita.create()
                        setEntity(newV)
                        navigate("/visita");
                    },
                },
                {
                    uid: "salvarForm",
                    text: "Salvar",
                    iconName: "Check",
                    sequence: "20",
                    action: () => {
                        form.submit();
                    },
                },

                {
                    uid: "salvarForm",
                    text: "Salvar e Imprimir",
                    iconName: "Print",
                    sequence: "20",
                    action: () => {
                        salvarImprimir();
                    },
                },
                {
                    uid: "sairForm",
                    text: "Sair",
                    iconName: "Close",
                    sequence: "999999",
                    action: () => {
                        // TODO: exibir diálogo de confirmação se o form estiver dirty.
                        navigate("/ConsultaVisitasRelatorios"); // estava redirecionando para a página inicial ao fechar
                    },
                },
            ];

            setActionsView(actions);
        }
        return () => {
            setActionsView([]);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, navigate, setActionsView, entity.loading]);

    useSetTitle("Visita Técnica");

    async function handleChangeSafra(state) {
        if (state.value) {
            if (!entity.data.safra || entity.data.safra.uid !== state.value.uid)
                entity.data.safra = state.value
        }

    }

    async function handleChangeCultura(state) {
        if (state.value) {
            if (!entity.data.cultura || entity.data.cultura.uid !== state.value.uid)
                entity.data.cultura = state.value
        }
    }
    async function handleFazendaChange(state) {
        // console.log('state', state)
        let values = form.getValues()
        entity.update(values)
        // Atualiza fazenda e cliente para permitir cadastro de novos apontamentos em uma nova visita.
        if (state.value) {
            try {

                await entity.loadFazenda(state.value);
                state.form.change("cliente", entity.data.cliente);
                setEntity(entity)
            } catch (error) {
                console.log('erro ao carregar fazenda')
                showWarning(error.message)
                state.form.change("fazenda", null);
                state.form.change("cliente", null);
            }
        } else {
            if (state.form) {
                state.form.change("cliente", null);
            }
        }
    }

    async function handleSubmit(values) {
        let valid = await validationLaudoVisita(values)
        if (valid) showWarning(valid)

        const valuesEntity = _.cloneDeep(values);
        entity.update(values);
        try {
            const success = await entity.save();

            if (success) {
                showSuccess("Laudo salvo com sucesso.");
                const diff = shallowDiff(valuesEntity, entity.data);
                searchParams.set("uid", entity.data.uid);
                setSearchParams(searchParams);
            } else {
                console.log('Erro ao salvar')
            }
        } catch (error) {
            let str = JSON.stringify(error);
            console.log('str ERROR', str)
            console.log('error', error)
            if (error.code === "invalid-argument") {
                str = "Erro de formado de dados na visita. Verifique os parametros do cliente"
            }
            console.log('str ERROR final', str)
            showError(str)
        }
    }

    function handleRemoveImage(image) {
        if (image.imagem) {
            entity.deleteImage(image.imagem);
        }
    }

    async function salvarImprimir() {
        let values = form.getValues()
        flog("Salvar/Gerar Relatório - Laudo Visita - " + values.uid);
        await handleSubmit(values);

        entity.update(values)
        new LaudoVisitaReport(entity.data.uid, entity).generate();
    }
    if (entity.loading) {
        return <div style={{ display: 'flex', justifyContent: "center", padding: 25, alignItems: 'center' }}><CircularProgress />Carregando Visita...</div>
    }

    async function forceIndex() {
        let values = form.getValues()
        if (!values.sequencialIndex) {
            let ref = getCollectionApi("Sequencial").doc('sSCy71ONYFEwlGVDK4kK');
            let v = await ref.get();
            console.log('v', v.data())
            let index = (v.data().index || 0) + 1;
            console.log('index', index)
            ref.set({ index }, { merge: true });

            const entity2 = new LaudoVisita();
            await entity2.update({ uid: values.uid, sequencialIndex: index });
            await entity2.save();
            console.log('sequencialIndex atualizado para:', index);


        }

    }
    const onChangeTab = (tab) => {
        
    }

    return (
        <FForm formApi={form} onSubmit={handleSubmit} initialValues={entity.data} sx={{ mt: 1 }} style={{ display: "flex", flex: 1, height: "100%" }}>
            <TabContainer onChangeTab={onChangeTab} style={{ height: "100%" }}>
                <TabPanel label="Dados" sx={{ m: 0 }} style={{ height: "100%" }}>
                    <FormContent>
                        <FormRow>
                            <DebugSetVisita change={form?.api?.change} values={form.getValues()} />
                            <DebugVisitaConsole values={form.getValues()} />
                            {/* <Button variant="outlined" onClick={forceIndex} sx={{ mb: 2 }} >
                                Forçar Sequencial Index
                            </Button> */}

                        </FormRow>
                        <FormRow>
                            <Box sx={{ display: "flex", gap: 2, flex: 2 }} >
                                <TextInput name="sequencialIndex" label="Visita Nº" disabled />
                                <DateInput name="data" label="Data da Visita" required />
                            </Box>


                            <SelectInput
                                name="status"
                                label="Status"
                                options={["Em Digitação", "Concluído", "Cancelado"]}
                            />
                            <TextInput sx={{ flex: 3 }} name="tecnico.nome" label="Técnico" disabled />
                        </FormRow>

                        <FormRow>
                            <SeletorFazenda
                                name="fazenda"
                                label="Fazenda"
                                EntityClass={Fazenda}
                                fieldFilterAdd={"cliente.nome"}
                                onChange={handleFazendaChange}
                                required

                            />

                            {/* <EntitySearchInput name="cliente" label="Cliente" EntityClass={Cliente} required  /> */}
                            <TextInput name="cliente.nome" label="Cliente" disabled />
                            {/* {console.log('entity', entity)} */}
                            <Anexo
                                folder={"metodologiasAvaliacao"}
                                label={"Ver Anexo da Metodologia de Avaliação"}
                                name={"cliente.anexos"}
                                // value={entity.cliente?.data?.anexos}
                                // value={entity.data?.cliente?.anexos}
                                viewOnly
                            />
                        </FormRow>

                        <FormRow>
                            <TextInput sx={{ flex: 2 }} name="responsavel" label="Responsável" />
                            <Box sx={{ display: "flex", gap: 2, flex: 2 }} >
                                <EntitySearchInput name="safra" label="Safra"
                                    sortResult={(a, b) => a.safra > b.safra ? 1 : -1}
                                    onChange={handleChangeSafra} EntityClass={Safra} required />
                                <EntitySearchInput name="cultura" label="Cultura" onChange={handleChangeCultura} EntityClass={Cultura} required />
                            </Box>
                        </FormRow>
                    </FormContent>
                </TabPanel>

                <TabPanel label="Apontamentos" style={{ height: "100%" }}>
                    <ApontamentoEditor visita={entity} updateForce form={form} />
                </TabPanel>

                <TabPanel label="Mapa" style={{ height: "100%" }}>
                    <ApontamentoEditor visita={entity} updateForce form={form} showMap={true} />
                </TabPanel>

                <TabPanel label="Parecer" sx={{ m: 2 }}>
                    <FormContent>
                        <FormRow>
                            <TextInput name="parecer" label="Parecer Técnico" multiline rows={6} />
                        </FormRow>

                        <FormRow>
                            <ImageInput
                                name="itensImagemPtecnico"
                                geolocationField="georeferencia"
                                multiple
                                onRemove={handleRemoveImage}
                            >
                                <FormRow>
                                    <TextInput name="descricao" label="Descrição" multiline rows={4} />
                                </FormRow>

                                <FormRow>
                                    <CheckboxInput name="confirmaRelatorio" label="Relatório" />
                                </FormRow>
                            </ImageInput>
                        </FormRow>
                    </FormContent>
                </TabPanel>

                {visitaUid && (
                    <TabPanel label="Resumo" sx={{ m: 2 }}>

                        {/* <FormContent>
                        <FormRow>
                            <TextInput name="parecer" label="Parecer Técnico" multiline rows={6} />
                        </FormRow>

                        <FormRow>
                            <ImageInput
                                name="itensImagemPtecnico"
                                geolocationField="georeferencia"
                                multiple
                                onRemove={handleRemoveImage}
                            >
                                <FormRow>
                                    <TextInput name="descricao" label="Descrição" multiline rows={4} />
                                </FormRow>

                                <FormRow>
                                    <CheckboxInput name="confirmaRelatorio" label="Relatório" />
                                </FormRow>
                            </ImageInput>
                        </FormRow>
                    </FormContent> */}
                    </TabPanel>
                )}
            </TabContainer>
            <DebugObjComp entity={entity} colection={"ValueCustomForm/sSCy71ONYFEwlGVDK4kK/Values"} uidObj={entity.data.uid ? entity.data.uid : ""} />
        </FForm >
    );
}
